/**
 * ViewGrid Toolbar Component
 *
 * Toolbar with Add Record button, Export button, Import CSV button,
 * Bulk Delete button, and other controls.
 * Based on original MyVault ViewGrid toolbar patterns.
 */

import React from "react";
import { Box, Button, Tooltip } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ExportButton from "./ExportButton";

interface ViewGridToolbarProps {
  viewId: string;
  onAddRecord?: () => void;
  canAddRecord?: boolean;
  addRecordDisabledReason?: string;
  isAddingRecord?: boolean;
  isLoading?: boolean;
  onAuditLog?: () => void;
  onImportCsv?: () => void;
  selectedCount?: number;
  onBulkDelete?: () => void;
}

export default function ViewGridToolbar({
  viewId,
  onAddRecord,
  canAddRecord = true,
  addRecordDisabledReason,
  isAddingRecord = false,
  isLoading = false,
  onAuditLog,
  onImportCsv,
  selectedCount = 0,
  onBulkDelete,
}: ViewGridToolbarProps) {
  const isBusy = isAddingRecord || isLoading;

  const addButton = (
    <Button
      variant="solid"
      color="primary"
      size="sm"
      startDecorator={<AddIcon />}
      onClick={onAddRecord}
      disabled={!canAddRecord || isBusy || !onAddRecord}
      loading={isBusy}
    >
      Add Record
    </Button>
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
        mb: 2,
      }}
    >
      {/* Bulk delete button - visible when rows are selected */}
      {selectedCount > 0 && onBulkDelete && (
        <Button
          variant="solid"
          color="danger"
          size="sm"
          startDecorator={<DeleteIcon />}
          onClick={onBulkDelete}
        >
          Delete Selected ({selectedCount})
        </Button>
      )}
      {addRecordDisabledReason && !canAddRecord ? (
        <Tooltip title={addRecordDisabledReason}>{addButton}</Tooltip>
      ) : (
        addButton
      )}
      {onImportCsv && (
        <Button
          variant="outlined"
          color="neutral"
          size="sm"
          startDecorator={<FileUploadIcon />}
          onClick={onImportCsv}
        >
          Import CSV
        </Button>
      )}
      <ExportButton viewId={viewId} />
      {onAuditLog && (
        <Button
          variant="outlined"
          color="neutral"
          size="sm"
          startDecorator={<HistoryIcon />}
          onClick={onAuditLog}
        >
          Audit Log
        </Button>
      )}
    </Box>
  );
}
